import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Ensure cookies are sent with every request (session cookie for main-domain users)
axios.defaults.withCredentials = true;

// ---- Bearer token fallback for iframe / mobile WebView contexts where
// third-party cookies are blocked (e.g. the Emergent Preview panel) ----
const TOKEN_KEY = 'mhy_token';

const applyToken = (token) => {
  if (token) {
    try { localStorage.setItem(TOKEN_KEY, token); } catch (_) {}
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    try { localStorage.removeItem(TOKEN_KEY); } catch (_) {}
    delete axios.defaults.headers.common.Authorization;
  }
};

// Hydrate the header from localStorage before any request is made.
try {
  const stored = localStorage.getItem(TOKEN_KEY);
  if (stored) axios.defaults.headers.common.Authorization = `Bearer ${stored}`;
} catch (_) {}

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch (e) {
      setUser(null);
      // If the bearer token was rejected, clear it so we don't keep retrying
      if (e?.response?.status === 401) applyToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (res.data?.token) applyToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    if (res.data?.token) applyToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (_) {}
    applyToken(null);
    setUser(null);
  };

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, startGoogleLogin, checkAuth, setUser, applyToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
